import { Image as ImageIcon, Paperclip } from "lucide-react";
import { clsx } from "clsx";
import type { ChatMessageReplyBrief } from "../types";

interface ReplyQuoteProps {
  reply: ChatMessageReplyBrief;
  mine: boolean;
  onJump?: (id: string) => void;
}

function isImage(contentType: string | undefined): boolean {
  return !!contentType && contentType.startsWith("image/");
}

export function ReplyQuote({ reply, mine, onJump }: ReplyQuoteProps) {
  // Cytat sprzed dołączenia oglądającego: serwer nie przysyła ani treści, ani
  // autora. Pokazujemy sam fakt odpowiedzi — i nie dajemy skoku do wiadomości,
  // której i tak nie ma w historii.
  if (reply.hidden) {
    return (
      <div
        className={clsx(
          "mb-1 w-full border-l-2 px-2 py-1 text-left text-[12px] italic",
          mine
            ? "border-red-400 bg-white/70 text-gray-500"
            : "border-gray-400 bg-gray-100 text-gray-500",
        )}
      >
        Odpowiedź na wcześniejszą wiadomość
      </div>
    );
  }
  const senderName =
    reply.sender?.full_name || reply.sender?.username || "(nieznany)";
  const firstAttachment = reply.attachments?.[0] ?? null;
  const extraCount = Math.max(0, (reply.attachments?.length ?? 0) - 1);
  const attachmentIsImage = firstAttachment && isImage(firstAttachment.content_type);
  const fallback = attachmentIsImage
    ? "📷 Obraz" + (extraCount > 0 ? ` +${extraCount}` : "")
    : firstAttachment
      ? firstAttachment.original_filename +
        (extraCount > 0 ? ` +${extraCount}` : "")
      : "";
  const preview = reply.deleted_at
    ? "[wiadomość usunięta]"
    : reply.body || fallback;
  return (
    <button
      type="button"
      onClick={() => onJump?.(reply.id)}
      className={clsx(
        "mb-1 flex w-full gap-2 border-l-2 px-2 py-1 text-left text-[12px] transition-opacity hover:opacity-80",
        mine
          ? "border-red-500 bg-white/70 text-gray-800"
          : "border-red-600 bg-gray-100 text-gray-800",
      )}
    >
      {firstAttachment?.url && attachmentIsImage ? (
        <img
          src={firstAttachment.url}
          alt=""
          className="w-9 h-9 rounded object-cover flex-shrink-0"
        />
      ) : firstAttachment ? (
        <div
          className={clsx(
            "flex h-9 w-9 flex-shrink-0 items-center justify-center",
            mine ? "bg-red-100" : "bg-gray-200",
          )}
        >
          <Paperclip size={12} />
        </div>
      ) : null}
      <div className="flex-1 min-w-0">
        <div
          className={clsx(
            "truncate text-[11px] font-semibold",
            mine ? "text-red-700" : "text-red-700",
          )}
        >
          {senderName}
        </div>
        <div className="truncate flex items-center gap-1">
          {attachmentIsImage && !reply.body && (
            <ImageIcon size={11} className="opacity-70" />
          )}
          <span className="truncate">{preview}</span>
        </div>
      </div>
    </button>
  );
}
